"use client";

import { clientSupabase } from "@/lib/supabase/browser";
import { useUser } from "@/lib/auth";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";

export function NotificationBell() {
  const { isLoaded, isSignedIn, user } = useUser();
  const supabase = clientSupabase();

  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasEverLoaded, setHasEverLoaded] = useState<boolean>(false);

  const hasUnread = unreadCount > 0;

  const fetchUnreadCount = useCallback(
    async (userId: string) => {
      setLoading(true);
      try {
        const { count, error } = await supabase
          .from("notifications")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("read", false);

        if (error) {
          // Soft-fail: don’t blow up the UI for a count issue
          console.warn("Failed to fetch notification count:", error.message);
          setUnreadCount(0);
        } else {
          setUnreadCount(count ?? 0);
        }

        setHasEverLoaded(true);
      } finally {
        setLoading(false);
      }
    },
    [supabase],
  );

  // Initial fetch when user context is ready
  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn || !user?.id) {
      setUnreadCount(0);
      setHasEverLoaded(false);
      return;
    }

    void fetchUnreadCount(user.id);
  }, [isLoaded, isSignedIn, user?.id, fetchUnreadCount]);

  // Realtime subscription: keep count in sync
  useEffect(() => {
    if (!isSignedIn || !user?.id) return;

    const userId = user.id;

    const channel = supabase
      .channel(`notifications:user:${userId}`)
      .on(
        "postgres_changes",
        {
          schema: "public",
          table: "notifications",
          event: "*",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          // Re-fetch count whenever something changes for this user
          void fetchUnreadCount(userId);
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [isSignedIn, user?.id, supabase, fetchUnreadCount]);

  // If the auth state isn’t ready or the user is signed out, don’t render the bell
  if (!isLoaded || !isSignedIn || !user?.id) {
    return null;
  }

  const label = hasUnread
    ? `You have ${unreadCount > 9 ? "9 or more" : unreadCount} unread notifications`
    : "Notifications";

  return (
    <Link
      href="/notifications"
      aria-label={label}
      title={label}
      className="relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-transparent text-muted-foreground transition hover:border-muted hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand,#1D3557)] focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      {/* Bell icon */}
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
        <path
          d="M12 2.75a4.75 4.75 0 0 0-4.75 4.75v2.3c0 .424-.142.836-.404 1.17L5.3 13.79a1 1 0 0 0 .78 1.62h11.84a1 1 0 0 0 .78-1.62l-1.546-2.82a1.95 1.95 0 0 1-.404-1.17V7.5A4.75 4.75 0 0 0 12 2.75Z"
          fill="currentColor"
        />
        <path d="M9.75 17.75a2.25 2.25 0 1 0 4.5 0h-4.5Z" fill="currentColor" />
      </svg>

      {/* Unread badge */}
      {hasUnread && (
        <span className="absolute -right-0.5 -top-0.5 inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white shadow-sm">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}

      {/* Subtle “pulse” ring for first-time unread state */}
      {hasUnread && !loading && hasEverLoaded && (
        <span className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-red-500/70 ring-offset-2 ring-offset-background animate-[ping_1.6s_ease-out_1]" />
      )}

      {/* Screen-reader only loading text; visually we keep it clean */}
      {loading && !hasUnread && <span className="sr-only">Checking notifications…</span>}
    </Link>
  );
}
